import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchIndizesApi, fetchLiveAktienByIndexApi } from './aktienApi';

/**
 * Redux-Slice fuer Indizes und ihre Aktien.
 *
 * Die Index-Liste kommt aus der Backend-Konfiguration. Die Aktien werden bei
 * jeder Auswahl LIVE von OnVista geholt (kein DB-Schreiben) - das Speichern
 * uebernimmt allein der taegliche 0-Uhr-Lauf im Backend.
 *
 * @typedef {import('types/dto').IndexDTO} IndexDTO
 * @typedef {import('types/dto').AktieUebersichtDTO} AktieUebersichtDTO
 */

export const fetchIndizes = createAsyncThunk('indizes/fetchIndizes', async () => {
  return (await fetchIndizesApi()) ?? [];
});

export const fetchAktienByIndex = createAsyncThunk(
  'indizes/fetchAktienByIndex',
  async (indexName) => {
    const aktien = (await fetchLiveAktienByIndexApi(indexName)) ?? [];
    return { indexName, aktien };
  }
);

const initialState = {
  indizes: [],
  indizesStatus: 'idle', // idle | loading | succeeded | failed
  selectedIndex: null,
  aktien: [],
  aktienStatus: 'idle',
  error: null,
};

const indizesSlice = createSlice({
  name: 'indizes',
  initialState,
  reducers: {
    selectIndex(state, action) {
      state.selectedIndex = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIndizes.pending, (state) => {
        state.indizesStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchIndizes.fulfilled, (state, action) => {
        state.indizesStatus = 'succeeded';
        state.indizes = action.payload;
        // Standardmaessig den ersten Index vorauswaehlen.
        if (!state.selectedIndex && action.payload.length > 0) {
          state.selectedIndex = action.payload[0].name;
        }
      })
      .addCase(fetchIndizes.rejected, (state, action) => {
        state.indizesStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAktienByIndex.pending, (state) => {
        state.aktienStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchAktienByIndex.fulfilled, (state, action) => {
        state.aktienStatus = 'succeeded';
        state.selectedIndex = action.payload.indexName;
        state.aktien = action.payload.aktien;
      })
      .addCase(fetchAktienByIndex.rejected, (state, action) => {
        state.aktienStatus = 'failed';
        state.error = action.error.message;
      });
  },
});

export const { selectIndex } = indizesSlice.actions;

export const selectAllIndizes = (state) => state.indizes.indizes;
export const selectIndizesStatus = (state) => state.indizes.indizesStatus;
export const selectSelectedIndex = (state) => state.indizes.selectedIndex;
export const selectIndexAktien = (state) => state.indizes.aktien;
export const selectAktienStatus = (state) => state.indizes.aktienStatus;

export default indizesSlice.reducer;
